import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Shield, 
  ShieldCheck, 
  Crown, 
  Trash2, 
  Send,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import axios from 'axios';

// Dynamically determine backend URL
const getBackendURL = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  return window.location.origin;
};

const BACKEND_URL = getBackendURL();
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);  
  const [inviteLoading, setInviteLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'member',
    firstName: '',
    lastName: ''
  });

  const { currentOrganization, user, token } = useAuth();

  useEffect(() => {
    // Only fetch data when currentOrganization is available
    if (currentOrganization) {
      fetchUsers();
      fetchInvitations();
    }
  }, [currentOrganization]);

  const fetchUsers = async () => {
    if (!currentOrganization) {
      console.log('No current organization available for fetching users');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/organizations/${currentOrganization.id}/users`);
      // Backend returns OrganizationMembersResponse with members array
      const membersData = response.data?.members || [];
      // Ensure it's always an array
      setUsers(Array.isArray(membersData) ? membersData : []);
    } catch (err) {
      setError('Failed to fetch users');
      // Ensure users is always an array even on error
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    if (!currentOrganization) {
      console.log('No current organization available for fetching invitations');
      return;
    }

    try {
      const response = await axios.get(`${API}/organizations/${currentOrganization.id}/invitations`);
      setInvitations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setError('');
    setSuccess('');

    try {
      // Convert camelCase to snake_case for backend
      const inviteData = {
        email: inviteForm.email,
        first_name: inviteForm.firstName,
        last_name: inviteForm.lastName,
        role: inviteForm.role
      };
      
      await axios.post(`${API}/organizations/${currentOrganization.id}/invite`, inviteData);
      
      setSuccess(`Invitation sent to ${inviteForm.email}`);
      setInviteForm({ email: '', role: 'member', firstName: '', lastName: '' });
      
      // Refresh invitations list
      fetchInvitations();
    } catch (err) {
      // Handle validation errors from Pydantic
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Pydantic validation errors
          const errorMessages = err.response.data.detail.map(e => e.msg).join(', ');
          setError(errorMessages);
        } else {
          setError(err.response.data.detail);
        }
      } else {
        setError('Failed to send invitation');
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName} from the organization?`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/organizations/${currentOrganization.id}/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`User ${userName} removed successfully`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleCancelInvitation = async (invitationId, email) => {
    if (!window.confirm(`Are you sure you want to cancel the invitation for ${email}?`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/organizations/${currentOrganization.id}/invitations/${invitationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Invitation for ${email} cancelled successfully`);
      fetchInvitations();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  const handleChangeRole = async (userId, newRole, userName) => {
    try {
      await axios.put(`${API}/organizations/${currentOrganization.id}/users/${userId}/role`, {
        role: newRole
      });
      setSuccess(`${userName}'s role updated to ${newRole}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user role');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Crown className="w-4 h-4" />;
      case 'admin': return <ShieldCheck className="w-4 h-4" />;
      case 'manager': return <Shield className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'admin': return 'bg-red-100 text-red-800 border-red-300';
      case 'manager': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getInviteStatusIcon = (status) => {
    switch (status) {
      case 'accepted': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'expired': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {!currentOrganization ? (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Organization Selected</h3>
          <p className="text-gray-600">Please select an organization to manage users</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
              <p className="text-gray-600">{currentOrganization?.name} • Manage team members and permissions</p>
            </div>
            <Badge variant="outline" className="px-3 py-1">
              <Users className="w-4 h-4 mr-1" />
              {users.length} Members
            </Badge>
          </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Invite User Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-purple-600" />
            Invite New User
          </CardTitle>
          <CardDescription>
            Send an invitation to add a new team member to {currentOrganization?.name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInviteUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={inviteForm.firstName}
                  onChange={(e) => setInviteForm({...inviteForm, firstName: e.target.value})}
                  placeholder="Enter first name"
                  required
                  disabled={inviteLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={inviteForm.lastName}
                  onChange={(e) => setInviteForm({...inviteForm, lastName: e.target.value})}
                  placeholder="Enter last name"
                  required
                  disabled={inviteLoading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                placeholder="Enter email address"
                required
                disabled={inviteLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <select
                id="role"
                value={inviteForm.role}
                onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md bg-white"
                disabled={inviteLoading}
              >
                <option value="member">Member - Basic access</option>
                <option value="manager">Manager - Can manage events and settings</option>
                <option value="admin">Admin - Full access except user management</option>
              </select>
            </div>

            <Button
              type="submit"
              disabled={inviteLoading || !inviteForm.email || !inviteForm.firstName || !inviteForm.lastName}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {inviteLoading ? (
                'Sending...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Current Users */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>Current members of {currentOrganization?.name}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading team members...</p>
            </div>
          ) : (!users || !Array.isArray(users) || users.length === 0) ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No team members yet. Invite your first member!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((member) => (
                <div key={member.user_id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 font-semibold">
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {member.first_name} {member.last_name}
                        {member.user_id === user?.id && <span className="text-sm text-purple-600 ml-2">(You)</span>}
                      </h4>
                      <p className="text-sm text-gray-600">{member.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Badge className={getRoleColor(member.role)}>
                      {getRoleIcon(member.role)}
                      <span className="ml-1 capitalize">{member.role.replace('_', ' ')}</span>
                    </Badge>
                    
                    {(member.user_id !== user?.id || member.role === 'super_admin') && (
                      <div className="flex space-x-2">
                        <select
                          value={member.role}
                          onChange={(e) => handleChangeRole(member.user_id, e.target.value, `${member.first_name} ${member.last_name}`)}
                          className="text-sm p-1 border border-gray-300 rounded"
                        >
                          <option value="member">Member</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Admin</option>
                          {member.role === 'super_admin' && (
                            <option value="super_admin">Super Admin</option>
                          )}
                        </select>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveUser(member.user_id, `${member.first_name} ${member.last_name}`)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Invitations</CardTitle>
            <CardDescription>Invitations waiting for response</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invitations.map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{invitation.email}</h4>
                      <p className="text-sm text-gray-600">
                        Invited {new Date(invitation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Badge className={getRoleColor(invitation.role)}>
                      {getRoleIcon(invitation.role)}
                      <span className="ml-1 capitalize">{invitation.role.replace('_', ' ')}</span>
                    </Badge>
                    
                    <div className="flex items-center space-x-1">
                      {getInviteStatusIcon(invitation.status)}
                      <span className="text-sm text-gray-600 capitalize">{invitation.status}</span>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelInvitation(invitation.id, invitation.email)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Cancel invitation"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
        </>
      )}
    </div>
  );
};

export default UserManagement;